/**
 * Deal list configuration.
 * Extracted from DealsList.tsx to enable generic listing component.
 *
 * Uses declarative column definitions with kind field:
 * - kind: 'object_name' - Renders avatar + display name together
 * - kind: 'property' - Renders property value using PropertyValueLabel
 * - kind: 'association' - Renders associated objects using CrmObjectTagGroup
 */

import type { CrmObject, QuantityValue } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { ListPageConfig } from '../../core/types';
import { getEntityTypeCreateConfig } from '../../record/registry/entityTypeRegistry';
import type { NumericValue } from '../utils/aggregationTransformers';
import { formatCurrencyValue } from '../utils/aggregationTransformers';

const createConfig = getEntityTypeCreateConfig('ahoy_deal');

export const dealListConfig: ListPageConfig = {
  entityType: 'ahoy_deal',
  title: 'Deal',
  pluralTitle: 'Deals',

  // Column configuration for table view - declarative format
  // Labels are derived from property/association schemas
  columns: [
    { kind: 'object_name', sortable: true, width: 280 },
    { kind: 'association', name: 'ahoy_deal_to_deal_pipeline' },
    { kind: 'association', name: 'ahoy_deal_to_deal_pipeline_stage' },
    { kind: 'property', name: 'ahoy_amount' },
    { kind: 'association', name: 'ahoy_deal_to_company' },
    { kind: 'association', name: 'ahoy_deal_to_contact' },
    { kind: 'property', name: 'ahoy_close_date' },
    { kind: 'property', name: 'ahoy_updated_at' },
  ],

  // Properties and associations from registry (single source of truth)
  createProperties: createConfig?.createProperties ?? [],
  createAssociations: createConfig?.createAssociations ?? [],

  // Metrics configuration
  metrics: [
    {
      label: 'Total Deals',
      calculate: (_data: CrmObject[], total: number) => total,
    },
    {
      label: 'Total Value',
      // Local reduction over loaded CrmObject rows — no aggregation API call, no convert_to.
      // Groups by ahoy_potential_amount.unit so multi-currency deals display pipe-separated
      // (e.g. "$100,000 | €50,000"). Unified single-currency display would require threading
      // exchange-rate context through MetricDefinition.calculate (out of scope here).
      calculate: (data: CrmObject[]) => {
        // ahoy_potential_amount is a formula field: ahoy_amount * ceil(stage probability)
        const sums = new Map<string, number>();
        for (const deal of data) {
          const q = getPropertyValue<QuantityValue>(
            deal.properties?.ahoy_potential_amount
          );
          if (!q) {
            continue;
          }
          const amount =
            typeof q.amount === 'string' ? parseFloat(q.amount) : q.amount;
          if (!Number.isFinite(amount)) {
            continue;
          }
          const unit = q.unit || 'USD';
          sums.set(unit, (sums.get(unit) ?? 0) + amount);
        }
        const values: NumericValue[] = Array.from(sums.entries()).map(
          ([unit, amount]) => ({ amount, unit })
        );
        return formatCurrencyValue(values);
      },
    },
    {
      label: 'Weighted Value',
      // Local reduction over loaded CrmObject rows — no aggregation API call, no convert_to.
      // Groups by ahoy_weighted_amount.unit so multi-currency deals display pipe-separated.
      // Unified single-currency display would require threading exchange-rate context
      // through MetricDefinition.calculate (out of scope here).
      calculate: (data: CrmObject[]) => {
        // ahoy_weighted_amount is a formula field: ahoy_amount * stage probability
        const sums = new Map<string, number>();
        for (const deal of data) {
          const q = getPropertyValue<QuantityValue>(
            deal.properties?.ahoy_weighted_amount
          );
          if (!q) {
            continue;
          }
          const amount =
            typeof q.amount === 'string' ? parseFloat(q.amount) : q.amount;
          if (!Number.isFinite(amount)) {
            continue;
          }
          const unit = q.unit || 'USD';
          sums.set(unit, (sums.get(unit) ?? 0) + amount);
        }
        const values: NumericValue[] = Array.from(sums.entries()).map(
          ([unit, amount]) => ({ amount, unit })
        );
        return formatCurrencyValue(values);
      },
    },
  ],

  // Default filters
  defaultFilters: {
    stage_name: undefined,
    page: 1,
    page_size: 20,
    sort: 'ahoy_created_at',
    order: 'desc',
  },

  // Pipeline configuration
  stageAssociationTypeName: 'ahoy_deal_to_deal_pipeline_stage',

  // UI configuration
  showTimeFilter: false,
};
